/**
 * Full-page card — DESIGN.md §13.1 (`/card/:num`).
 *
 * "global — multi-project cards, email deep links". Addressed by the card's
 * `#number` (D2), which is what makes a copied link stable across project
 * moves and readable in an email.
 *
 * CARD-18: this used to be a 768px column in the middle of a 1920px screen —
 * 60% of the display wasted on the surface people open when they want *more*
 * room, not less. The panel now lays itself out in two columns from `lg`
 * (`variant="page"`), and the page just gives it a sane maximum width.
 */
import { createFileRoute, useCanGoBack, useNavigate, useRouter } from '@tanstack/react-router';
import { z } from 'zod';
import { ArrowLeft } from 'lucide-react';
import { CardPanel } from '@/components/cards/card-panel';
import { Button } from '@/components/ui/button';
import { EmptyState } from '@/components/ui/empty-state';
import { LoadingRegion } from '@/components/ui/spinner';
import { useTaskByNumber } from '@/features/tasks/api';

export const Route = createFileRoute('/card/$number')({
  /**
   * #23 — `?focus=description` is set by the create flow, so a card you have
   * just made opens with the caret already in its body. In the URL rather than
   * router state so a reload does not re-steal focus and a shared link never
   * carries it by accident.
   */
  validateSearch: z.object({ focus: z.literal('description').optional() }),
  component: CardPage,
});

function CardPage(): JSX.Element {
  const { number } = Route.useParams();
  const { focus } = Route.useSearch();
  const navigate = useNavigate();
  const router = useRouter();
  const canGoBack = useCanGoBack();
  const parsed = Number(number);
  const {
    data: task,
    isLoading,
    isError,
  } = useTaskByNumber(Number.isInteger(parsed) ? parsed : null);

  if (isLoading) return <LoadingRegion label={`Loading #${number}`} />;

  if (isError || !task) {
    return (
      <div className="mx-auto w-full max-w-3xl p-6">
        <EmptyState
          title={`Card #${number} not found`}
          description="It may have been deleted, or the link may be wrong. Deleted cards can be restored by an administrator."
          action={
            <Button variant="secondary" onClick={() => void navigate({ to: '/projects' })}>
              Browse projects
            </Button>
          }
        />
      </div>
    );
  }

  /* Router history keeps the list's scroll position; `/projects` is the
     fallback for a deep link opened in a fresh tab. */
  const goBack = (): void =>
    canGoBack ? router.history.back() : void navigate({ to: '/projects' });

  return (
    <div className="flex h-[calc(100dvh-3.5rem)] w-full flex-col lg:h-dvh">
      {/*
        #51 — from `lg` up, Back is page chrome on its own line. Below it, the
        line goes and the button moves into the panel's own header row (see
        `onBack`), because three stacked bars — Back, the card's controls, the
        title — was the header taking a third of a short window before a word
        of the card had been read.
      */}
      <div className="mx-auto w-full max-w-[1400px] px-3 pt-3 max-lg:hidden">
        <Button
          variant="ghost"
          size="sm"
          className="[@media(pointer:coarse)]:h-11"
          onClick={goBack}
        >
          <ArrowLeft className="size-4" />
          Back
        </Button>
      </div>
      <div className="min-h-0 flex-1">
        <CardPanel
          taskId={task.id}
          variant="page"
          onBack={goBack}
          focusDescription={focus === 'description'}
        />
      </div>
    </div>
  );
}
